import {
  Document,
  Font,
  Image as PDFImage,
  Page,
  PDFDownloadLink,
  PDFViewer,
  StyleSheet,
  Text,
  View,
} from "@react-pdf/renderer";
import React from "react";
import { Button } from "@/components/ui/button";
import { Download } from "lucide-react";
import logo from "/public/logo.png";
import { FormValues } from "@/models/Sila.Animals.schema";

// Register fonts for better PDF rendering
Font.register({
  family: "Inter",
  src: "https://fonts.googleapis.com/css2?family=Ancizar+Sans:ital,wght@0,100..1000;1,100..1000&family=Roboto:ital,wght@0,100..900;1,100..900&display=swap",
});

const styles = StyleSheet.create({
  page: {
    flexDirection: "column",
    backgroundColor: "#ffffff",
    padding: 20,
    fontFamily: "Times-Roman",
    fontSize: 10,
  },
  container: {
    flex: 1,
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 20,
    paddingBottom: 10,
    borderBottom: "6 solid #fefce8",
  },
  logo: {
    width: "50%",
    height: 60,
  },
  title: {
    fontSize: 24,
    fontWeight: "bold",
    color: "#1f2937",
  },
  heroSection: {
    backgroundColor: "#fbfaf7",
    padding: 15,
    borderRadius: 8,
    marginBottom: 15,
    border: "3 solid #fefce8",
  },
  heroContent: {
    flexDirection: "row",
    alignItems: "center",
    gap: 15,
  },
  heroImage: {
    width: 120,
    height: 100,
    borderRadius: 8,
  },
  heroInfo: {
    flex: 1,
  },
  // animalName: {
  //   fontSize: 16,
  //   fontWeight: "bold",
  //   color: "#1e40af",
  //   marginBottom: 5,
  // },
  dgr: {
    fontSize: 14,
    color: "#374151",
    marginBottom: 10,
  },
  statsGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
  },
  statItem: {
    backgroundColor: "#ffffff",
    padding: 4,
    borderRadius: 6,
    border: "1 solid #fbfaf7",
    minWidth: 50,
  },
  statLabel: {
    fontSize: 4,
    color: "#000000",
    marginBottom: 2,
  },
  statValue: {
    fontSize: 5,
    fontWeight: "bold",
    color: "#000000",
  },
  section: {
    marginBottom: 15,
  },
  sectionHeader: {
    backgroundColor: "#3680d4",
    padding: 10,
    borderTopLeftRadius: 6,
    borderTopRightRadius: 6,
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    border: "1 solid #cbd5e1",
  },
  sectionTitle: {
    fontSize: 10,
    fontWeight: "bold",
    color: "#0f172a",
  },
  sectionContent: {
    backgroundColor: "#fbfaf7",
    padding: 2,
    border: "1 solid #cbd5e1",
    borderTop: "none",
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0,
    borderBottomLeftRadius: 6,
    borderBottomRightRadius: 6,
  },
  table: {
    width: "100%",
  },
  tableHeader: {
    flexDirection: "row",
    backgroundColor: "#fbfaf7",
    padding: 4,
    borderBottom: "1 solid #e2e8f0",
  },
  tableRow: {
    flexDirection: "row",
    padding: 4,
    borderBottom: "0.5 solid #f1f5f9",
  },
  tableCell: {
    flex: 1,
    fontSize: 5,
    color: "##fff06b",
  },
  tableCellHeader: {
    flex: 1,
    fontSize: 9,
    fontWeight: "bold",
    color: "#1f2937",
  },
  tableCellCenter: {
    flex: 1,
    fontSize: 9,
    color: "#374151",
    textAlign: "center",
  },
  infoGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 10,
  },
  infoItem: {
    minWidth: 120,
    marginBottom: 8,
  },
  infoLabel: {
    fontSize: 8,
    color: "#6b7280",
    marginBottom: 2,
  },
  infoValue: {
    fontSize: 10,
    fontWeight: "bold",
    color: "#111827",
  },
  badge: {
    backgroundColor: "#dbeafe",
    color: "#1e40af",
    paddingTop: 3,
    paddingBottom: 3,
    paddingLeft: 6,
    paddingRight: 6,
    borderRadius: 4,
    fontSize: 8,
    fontWeight: "bold",
  },
  imageGallery: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
  },
  galleryImage: {
    width: "100%",
    height: "100%",
    borderRadius: 4,
    border: "1 solid #8fee8f",
  },

  pedigreePage: {
    flexDirection: "column",
    backgroundColor: "#FFFFFF",
    padding: 15,
    fontFamily: "Helvetica",
  },
  pedigreeTitle: {
    fontSize: 16,
    fontWeight: "bold",
    textAlign: "center",
    marginBottom: 15,
    color: "#2D3748",
  },
  treeContainer: {
    flex: 1,
    position: "relative",
  },
  generation: {
    flexDirection: "row",
    gap: "5",
    justifyContent: "space-around",
    marginBottom: 25,
    alignItems: "center",
  },
  animalBox: {
    width: 80,
    minHeight: 50,
    border: "1px solid #E2E8F0",
    borderRadius: 4,
    padding: 4,
    backgroundColor: "#F7FAFC",
    alignItems: "center",
    justifyContent: "center",
  },
  highlightedBox: {
    width: 100,
    minHeight: 60,
    border: "2px solid #3182CE",
    borderRadius: 6,
    padding: 6,
    backgroundColor: "#EBF8FF",
    alignItems: "center",
    justifyContent: "center",
  },
  compactBox: {
    width: 55,
    minHeight: 40,
    border: "1px solid #E2E8F0",
    borderRadius: 3,
    padding: 3,
    backgroundColor: "#F7FAFC",
    alignItems: "center",
    justifyContent: "center",
  },
  animalName: {
    fontSize: 7,
    fontWeight: "bold",
    textAlign: "center",
    marginBottom: 1,
    color: "#2D3748",
  },
  animalInfo: {
    fontSize: 5,
    textAlign: "center",
    color: "#4A5568",
    marginBottom: 0.5,
  },
  label: {
    fontSize: 4,
    textAlign: "center",
    color: "#718096",
    fontStyle: "italic",
  },
  emptyBox: {
    width: 80,
    minHeight: 50,
    border: "1px dashed #CBD5E0",
    borderRadius: 4,
    padding: 4,
    backgroundColor: "#F7FAFC",
    alignItems: "center",
    justifyContent: "center",
  },
  emptyText: {
    fontSize: 6,
    textAlign: "center",
    color: "#A0AEC0",
  },
  connectingLines: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: -1,
  },
  legend: {
    marginTop: 10,
    padding: 8,
    backgroundColor: "#F7FAFC",
    borderRadius: 6,
    border: "1px solid #E2E8F0",
  },
  legendTitle: {
    fontSize: 10,
    fontWeight: "bold",
    marginBottom: 6,
    color: "#2D3748",
  },
  legendItem: {
    fontSize: 7,
    marginBottom: 2,
    color: "#4A5568",
  },
});

// 1. Accept animalData as a prop in SilaProfilePDF
// export interface AnimalData {
//   _id: string
//   dgr: string
//   birthDate: string
//   sex: string
//   name: string
//   age: number
//   adg: number
//   imageUrls: string[]
//   videos: Array<{
//     url: string
//     title: string
//     description: string
//     id: string
//     _id: string
//   }>
//   breed: string
//   bullType: string
//   ww: number
//   pw: number
//   yw: number
//   pmwg: number
//   coatColor: string
//   size: string
//   weight: number
//   paternalNucleus: string
//   paternalType: string
//   breedingType: string
//   vaccination: string[]
//   racialComposition: string
//   birthCondition: string
//   damName: string
//   damDGR: string
//   damBreed: string
//   sireDGR: string
//   sireName: string
//   sireBreed: string
//   sireBirthDate: string
//   paternalGrandDamDGR: string
//   paternalGrandDamName: string
//   paternalGrandDamBreed: string
//   paternalGrandSireDGR: string
//   paternalGrandSireName: string
//   paternalGrandSireBreed: string
//   animalStatus: string
//   birthWeight: number
//   cattleClass: string
//   phases: string
//   diagnosis: string
//   inseminationProgram: string
//   numberOffspring: number
//   indexEfficiency: number
//   evaluation: string
//   phaseIStatus: boolean
//   phaseIIStatus: boolean
//   phaseIIIStatus: boolean
//   length_of_rump: number
//   height_of_rump: number
//   lower_height: number
//   back_height: number
//   body_length: number
//   rib_depth: number
//   chest_perimeter: number
//   body_width: number
//   scrotal_circumference: number
//   mw_me_epd: number
//   mw_me_acc: number
//   mw_me_deca: number
//   bw_de_epd: number
//   bw_de_acc: number
//   bw_de_deca: number
//   ww_de_epd: number
//   ww_de_acc: number
//   ww_de_deca: number
//   aw_de_epd: number
//   aw_de_acc: number
//   aw_de_deca: number
//   yw_de_epd: number
//   yw_de_acc: number
//   yw_de_deca: number
//   sc365_epd: number
//   sc365_acc: number
//   sc365_deca: number
//   bs_epd: number
//   bs_acc: number
//   bs_deca: number
//   prec_epd: number
//   prec_acc: number
//   prec_deca: number
//   musc_epd: number
//   musc_acc: number
//   musc_deca: number
//   rea_epd: number
//   rea_acc: number
//   rea_deca: number
//   finishing_epd: number
//   finishing_acc: number
//   finishing_deca: number
//   mar_epd: number
//   mar_acc: number
//   mar_deca: number
//   iabcz_epd: number
//   iabcz_acc: number
//   iabcz_deca: number
//   createdAt: string
//   updatedAt: string
//   __v: number
// }

// SilaProfilePDF now accepts animalData as a prop
export const SilaProfilePDF: React.FC<{ animalData: FormValues }> = ({
  animalData,
}) => {
  // Performance metrics data
  const performanceMetricsData = [
    {
      key: "mw_me",
      label: "MW_ME",
      description: "Maternal Weight Maternal Effect",
      epd: animalData?.mw_me_epd ?? "",
      acc: animalData?.mw_me_acc ?? "",
      deca: animalData?.mw_me_deca ?? "",
    },
    {
      key: "bw_de",
      label: "BW_DE",
      description: "Birth Weight Direct Effect",
      epd: animalData?.bw_de_epd ?? "",
      acc: animalData?.bw_de_acc ?? "",
      deca: animalData?.bw_de_deca ?? "",
    },
    {
      key: "ww_de",
      label: "WW_DE",
      description: "Weaning Weight Direct Effect",
      epd: animalData?.ww_de_epd ?? "",
      acc: animalData?.ww_de_acc ?? "",
      deca: animalData?.ww_de_deca ?? "",
    },
    {
      key: "aw_de",
      label: "AW_DE",
      description: "Annual Weight Direct Effect",
      epd: animalData?.aw_de_epd ?? "",
      acc: animalData?.aw_de_acc ?? "",
      deca: animalData?.aw_de_deca ?? "",
    },
    {
      key: "yw_de",
      label: "YW_DE",
      description: "Yearling Weight Direct Effect",
      epd: animalData?.yw_de_epd ?? "",
      acc: animalData?.yw_de_acc ?? "",
      deca: animalData?.yw_de_deca ?? "",
    },
    {
      key: "sc365",
      label: "SC365",
      description: "Scrotal Circumference at 365 days",
      epd: animalData?.sc365_epd ?? "",
      acc: animalData?.sc365_acc ?? "",
      deca: animalData?.sc365_deca ?? "",
    },
    {
      key: "sc450",
      label: "SC450",
      description: "Scrotal Circumference at 450 days",
      epd: "",
      acc: "",
      deca: "",
    },
    {
      key: "bs",
      label: "BS",
      description: "Body Structure",
      epd: animalData?.bs_epd ?? "",
      acc: animalData?.bs_acc ?? "",
      deca: animalData?.bs_deca ?? "",
    },
  ];

  // Body measurements data
  const bodyMeasurementsData = [
    {
      key: "length_of_rump",
      label: "Length of Rump",
      value: animalData?.length_of_rump,
      unit: "cm",
    },
    {
      key: "height_of_rump",
      label: "Height of Rump",
      value: animalData?.height_of_rump,
      unit: "cm",
    },
    {
      key: "lower_height",
      label: "Lower Height",
      value: animalData?.lower_height,
      unit: "cm",
    },
    {
      key: "back_height",
      label: "Back Height",
      value: animalData?.back_height,
      unit: "cm",
    },
    {
      key: "body_length",
      label: "Body Length",
      value: animalData?.body_length,
      unit: "cm",
    },
    {
      key: "rib_depth",
      label: "Rib Depth",
      value: animalData?.rib_depth,
      unit: "cm",
    },
    {
      key: "chest_perimeter",
      label: "Chest Perimeter",
      value: animalData?.chest_perimeter,
      unit: "cm",
    },
    {
      key: "body_width",
      label: "Body Width",
      value: animalData?.body_width,
      unit: "cm",
    },
    {
      key: "scrotal_circumference",
      label: "Scrotal Circumference",
      value: animalData?.scrotal_circumference,
      unit: "cm",
    },
  ];

  // Prepare milkData for MilkClorifideTablePDF from animalData (using camelCase
  // fields)
  const milkData: MilkClorifideData[] = [
    {
      milk_kg: animalData?.milkKg,
      age1p: animalData?.age1P,
      ebi: animalData?.ebi,
      milk_acc: animalData?.milkAcc,
      age_1p_acc: animalData?.age1pAcc,
      ebi_acc: animalData?.ebiAcc,
      beta_lac: animalData?.betaLac,
      kappa_c: animalData?.kappaC,
      beta_c: animalData?.betaC,
    },
  ];

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.container}>
          {/* Header */}
          <View style={styles.header}>
            <PDFImage style={styles.logo} src={logo} />
            <Text style={styles.title}>Sila Green Genetics</Text>
          </View>

          {/* Hero Section */}
          <View style={styles.heroSection}>
            <View style={styles.heroContent}>
              {animalData?.imageUrls?.[0] && (
                <PDFImage
                  style={styles.heroImage}
                  src={animalData.imageUrls[0]}
                />
              )}
              <View style={styles.heroInfo}>
                <Text style={styles.animalName}>{animalData?.name}</Text>
                <Text style={styles.dgr}>DGR: {animalData?.dgr}</Text>

                <View style={styles.statsGrid}>
                  <View style={styles.statItem}>
                    <Text style={styles.statLabel}>Age</Text>
                    <Text style={styles.statValue}>
                      {animalData?.age} months
                    </Text>
                  </View>
                  <View style={styles.statItem}>
                    <Text style={styles.statLabel}>Weight</Text>
                    <Text style={styles.statValue}>{animalData?.weight}</Text>
                  </View>
                  <View style={styles.statItem}>
                    <Text style={styles.statLabel}>Sex</Text>
                    <Text style={styles.statValue}>{animalData?.sex}</Text>
                  </View>
                  <View style={styles.statItem}>
                    <Text style={styles.statLabel}>Birth Date</Text>
                    <Text style={styles.statValue}>
                      {animalData?.birthDate}
                    </Text>
                  </View>
                  <View style={styles.statItem}>
                    <Text style={styles.statLabel}>Size</Text>
                    <Text style={styles.statValue}>{animalData?.size}</Text>
                  </View>
                  <View style={styles.statItem}>
                    <Text style={styles.statLabel}>Coat Color</Text>
                    <Text style={styles.statValue}>
                      {animalData?.coatColor}
                    </Text>
                  </View>
                </View>
              </View>
            </View>
          </View>

          {/* Photo Gallery */}
          <View style={styles.section}>
            <View style={styles.sectionHeader}>
              <Text style={styles.sectionTitle}>Photo Gallery</Text>
            </View>
            <View style={styles.sectionContent}>
              {animalData?.imageUrls && animalData?.imageUrls.length > 0 ? (
                <View style={styles.imageGallery}>
                  {animalData.imageUrls.slice(0, 8).map((url, index) => (
                    <PDFImage
                      key={index}
                      style={styles.galleryImage}
                      src={url}
                    />
                  ))}
                </View>
              ) : (
                <Text>No images available for this animal</Text>
              )}
            </View>
          </View>

          {/* Basic Information for Beef Bulls */}
          {animalData.bullType === "Beef Bull" ? (
            <View style={styles.section} break>
              <View style={styles.sectionHeader}>
                <Text style={styles.sectionTitle}>Basic Information</Text>
              </View>
              <View style={styles.sectionContent}>
                <View style={styles.infoGrid}>
                  <View style={styles.infoItem}>
                    <Text style={styles.infoLabel}>EPD Birth Weight</Text>
                    <Text style={styles.infoValue}>
                      {animalData?.birthWeight}
                    </Text>
                  </View>
                  <View style={styles.infoItem}>
                    <Text style={styles.infoLabel}>EPD Weaning Weight</Text>
                    <Text style={styles.infoValue}>{animalData?.ww}</Text>
                  </View>
                  <View style={styles.infoItem}>
                    <Text style={styles.infoLabel}>EPD Post Weaning</Text>
                    <Text style={styles.infoValue}>{animalData?.pw}</Text>
                  </View>
                  <View style={styles.infoItem}>
                    <Text style={styles.infoLabel}>EPD Yearling Weight</Text>
                    <Text style={styles.infoValue}>{animalData?.yw}</Text>
                  </View>
                  <View style={styles.infoItem}>
                    <Text style={styles.infoLabel}>PMWG</Text>
                    <Text style={styles.infoValue}>{animalData?.pmwg}</Text>
                  </View>
                  <View style={styles.infoItem}>
                    <Text style={styles.infoLabel}>Status</Text>
                    <Text style={[styles.infoValue, styles.badge]}>
                      {animalData?.animalStatus}
                    </Text>
                  </View>
                  <View style={styles.infoItem}>
                    <Text style={styles.infoLabel}>Offspring</Text>
                    <Text style={styles.infoValue}>
                      {animalData?.numberOffspring}
                    </Text>
                  </View>
                </View>
              </View>
            </View>
          ) : (
            // <Page size="A4" style={ styles.page }>
            // <View style={ styles.container }>
            //   <View style={ styles.section }>
            //     <View style={ styles.sectionHeader }>
            //       <Text style={ styles.sectionTitle }>Milk Analysis</Text>
            //     </View>
            //     <View style={ styles.sectionContent }>
            //       <View style={ styles.table }>
            //         <View style={ styles.tableHeader }>
            //           <Text style={ styles.tableCellHeader }>Metric</Text>
            //           <Text style={ styles.tableCellHeader }>Value</Text>
            //         </View>
            //       </View>
            //     </View>
            //   </View>
            // </View>
            // </Page>
            <MilkClorifideTablePDF data={milkData} />
          )}
        </View>
      </Page>

      {/* Second Page for Performance Metrics */}
      {animalData.bullType === "Beef Bull" && (
        <Page size="A4" style={styles.page}>
          <View style={styles.container}>
            {/* Performance Metrics Table */}
            <View style={styles.section}>
              <View style={styles.sectionHeader}>
                <Text style={styles.sectionTitle}>Performance Metrics</Text>
              </View>
              <View style={styles.sectionContent}>
                <View style={styles.table}>
                  <View style={styles.tableHeader}>
                    <Text style={styles.tableCellHeader}>Metric</Text>
                    <Text style={styles.tableCellHeader}>EPD</Text>
                    <Text style={styles.tableCellHeader}>ACC</Text>
                    <Text style={styles.tableCellHeader}>DECA</Text>
                    <Text style={styles.tableCellHeader}>Description</Text>
                  </View>
                  {performanceMetricsData.map((metric) => (
                    <View key={metric.key} style={styles.tableRow}>
                      <Text style={styles.tableCell}>{metric.label}</Text>
                      <Text style={styles.tableCellCenter}>
                        {metric.epd || "N/A"}
                      </Text>
                      <Text style={styles.tableCellCenter}>
                        {metric.acc || "N/A"}
                      </Text>
                      <Text style={styles.tableCellCenter}>
                        {metric.deca || "N/A"}
                      </Text>
                      <Text style={styles.tableCell}>{metric.description}</Text>
                    </View>
                  ))}
                </View>
              </View>
            </View>

            {/* Body Measurements Table */}
            <View style={styles.section}>
              <View style={styles.sectionHeader}>
                <Text style={styles.sectionTitle}>Body Measurements</Text>
              </View>
              <View style={styles.sectionContent}>
                <View style={styles.table}>
                  <View style={styles.tableHeader}>
                    <Text style={styles.tableCellHeader}>Measurement</Text>
                    <Text style={styles.tableCellHeader}>Value</Text>
                    <Text style={styles.tableCellHeader}>Unit</Text>
                  </View>
                  {bodyMeasurementsData.map((measurement) => (
                    <View key={measurement.key} style={styles.tableRow}>
                      <Text style={styles.tableCell}>{measurement.label}</Text>
                      <Text style={styles.tableCellCenter}>
                        {measurement.value || "N/A"}
                      </Text>
                      <Text style={styles.tableCellCenter}>
                        {measurement.unit}
                      </Text>
                    </View>
                  ))}
                </View>
              </View>
            </View>
            <PedigreeTree animal={animalData} />
          </View>
        </Page>
      )}

      {/* Milk Analysis for Dairy Bulls */}
    </Document>
  );
};

// SilaAnimalsProfilePDF: get animalData from somewhere (for now, use the same
// hardcoded data as before)
export const SilaAnimalsProfilePDF = (animalData: FormValues) => {
  // Only keep what's needed for rendering
  // const animalData: AnimalData = {
  //   _id: "6869380f76be772cd4107a4f",
  //   dgr: "CJCJ 13914",
  //   birthDate: "August 17, 2020",
  //   sex: "Male",
  //   name: "LINEAR CJ SANT ANNA 13914",
  //   age: 58,
  //   adg: 0,
  //   imageUrls: [
  //     "https://firebasestorage.googleapis.com/v0/b/silagreen-86024.appspot.com/o/images%2FsilaAnimals%2F1751726085394-LINEAR%20CJCJ.png?alt=media&token=d68dbab7-fc1d-4bce-951c-da6988bcfbe4",
  // ], videos: [ { url: "https://youtu.be/SlvWFFQm88Q?si=EfEGc4xkVEtB-1Fc", title:
  // " LINEAR CJ SANT ANNA - Nelore Semen", description: "LINEAR CJ SANT ANNA 13914
  // offers outstanding genetic potential for breeders seeking a balanced,
  // high-performance Nelore bull. His progeny excel in growth, with calves showing
  // strong weaning and yearling weights, ensuring robust development and efficient
  // weight gain. Daughters inherit solid maternal traits, including improved
  // weaning weights for their own calves and a high likelihood of longevity in the
  // herd, while sons benefit from strong fertility and growth. The bull also
  // enhances carcass quality, contributing to better meat yield and desirable fat
  // cover for improved marbling. Additional advantages include early maturity, well-developed muscling, and a low inbreeding risk, making him an excellent choice for herds focused on productivity, durability, and meat quality. With elite ancestry and reliable performance across key traits, this bull’s semen is a valuable investment for sustainable, high-value beef production.  \n\n\nFor inquiries or semen orders: +234 -912800-6892 |  www.silagreengenetics.com", id: "1751726060418", _id: "6869380f76be772cd4107a50", }, ], breed: "Nelore", bullType: "Beef Bull", ww: 5.05, pw: 3.05, yw: 11.34, pmwg: 9, coatColor: "White", size: "Large Frame", weight: 0, paternalNucleus: " CIFRÃO FVC", paternalType: "Purebred", breedingType: "Natural", vaccination: [ "brucella", "cbpp", "fmd", "hsv", "ibr", "bvd", "brsv", "PI", "leptospirosis", "black quarter", "blue tongue", "annthrax", ], racialComposition: "Pure", birthCondition: "normal", damName: "CJ SANT ANNA ", damDGR: "CJ 12129", damBreed: "Nelore", sireDGR: "FVC10971", sireName: " CIFRÃO FVC", sireBreed: "Nelore", sireBirthDate: "October 27, 2015", paternalGrandDamDGR: "FVC9182", paternalGrandDamName: "HORDA FVC", paternalGrandDamBreed: "Nelore", paternalGrandSireDGR: "LBMND4685", paternalGrandSireName: "D4685 DA MN", paternalGrandSireBreed: "Nelore", animalStatus: "semen", birthWeight: 0, cattleClass: "bull", phases: "phase2", diagnosis: "healthy", inseminationProgram: "timed", numberOffspring: 9675, indexEfficiency: 15.47, evaluation: "UnAssigned", phaseIStatus: false, phaseIIStatus: false, phaseIIIStatus: false, length_of_rump: 0, height_of_rump: 0, lower_height: 0, back_height: 0, body_length: 0, rib_depth: 0, chest_perimeter: 0, body_width: 0, scrotal_circumference: 0, mw_me_epd: 3.05, mw_me_acc: 35, mw_me_deca: 1, bw_de_epd: -0.08, bw_de_acc: 51, bw_de_deca: 3, ww_de_epd: 5.05, ww_de_acc: 51, ww_de_deca: 3, aw_de_epd: 9, aw_de_acc: 49, aw_de_deca: 2, yw_de_epd: 11.34, yw_de_acc: 52, yw_de_deca: 2, sc365_epd: 0.9, sc365_acc: 39, sc365_deca: 1, bs_epd: -1.54, bs_acc: 37, bs_deca: 0, prec_epd: 4.19, prec_acc: 37, prec_deca: 1, musc_epd: 4.85, musc_acc: 37, musc_deca: 1, rea_epd: 112, rea_acc: 44, rea_deca: 3, finishing_epd: 5.29, finishing_acc: 31, finishing_deca: 1, mar_epd: 1.39, mar_acc: 31, mar_deca: 1, iabcz_epd: 16.74, iabcz_acc: 0, iabcz_deca: 1, createdAt: "2025-07-05T14:34:55.574Z", updatedAt: "2025-07-05T14:34:55.574Z", __v: 0, }

  return (
    <div className="w-full">
      <PDFControls
        showPDFViewer={false}
        setShowPDFViewer={() => {}}
        PDFComponents={{
          PDFDownloadLink,
          PDFViewer,
        }}
        animalData={animalData}
      />
    </div>
  );
};

// Update PDFControls to accept animalData as a prop and pass it to SilaProfilePDF
interface PDFControlsProps {
  showPDFViewer: boolean;
  setShowPDFViewer: (show: boolean) => void;
  PDFComponents: any;
  animalData: FormValues;
}

const PDFControls: React.FC<PDFControlsProps> = ({
  showPDFViewer,
  setShowPDFViewer,
  PDFComponents,
  animalData,
}) => {
  const PDFDocument = SilaProfilePDF;
  if (!PDFDocument) {
    return null;
  }
  const { PDFDownloadLink, PDFViewer } = PDFComponents;
  console.log("animalData", animalData);
  if (showPDFViewer) {
    return (
      <div className="w-full">
        <div className="mb-4 flex gap-2">
          <Button onClick={() => setShowPDFViewer(false)} variant="outline">
            Back to Controls
          </Button>
        </div>
        <div className="w-full h-[800px] border rounded-lg">
          <PDFViewer width="100%" height="100%">
            <PDFDocument animalData={animalData} />
          </PDFViewer>
        </div>
      </div>
    );
  }
  return (
    <div className="flex gap-2">
      <PDFDownloadLink
        document={<PDFDocument animalData={animalData} />}
        fileName={`${animalData.name || "animal"}-profile.pdf`}
      >
        {({ loading }: { loading: boolean }) => (
          <Button
            disabled={loading}
            className="flex items-center gap-2 relative left-10"
          >
            <Download className="w-4 h-4" />
            {loading ? "Generating PDF..." : "Download PDF"}
          </Button>
        )}
      </PDFDownloadLink>

      {/* <Button
				 onClick={ () => setShowPDFViewer( true ) }
				 variant="outline"
				 className="flex items-center gap-2"
				 >
				 <Eye className="w-4 h-4" />
				 Preview PDF
				 </Button> */}
    </div>
  );
};

// Define the animal data structure
interface Animal {
  id?: string;
  name?: string;
  breed?: string;
  dgr?: string;
  birthDate?: string;
  sex?: string;
  coatColor?: string;
  weight?: string;
  racialComposition?: string;
}

// Component to render individual animal boxes
const AnimalBox: React.FC<{
  animal: Animal | null;
  label?: string;
  highlight?: boolean;
  compact?: boolean;
}> = ({ animal, label, highlight = false, compact = false }) => {
  if (!animal || !animal.name) {
    return (
      <View style={compact ? styles.compactBox : styles.emptyBox}>
        <Text style={styles.emptyText}>No Data Available</Text>
      </View>
    );
  }

  const boxStyle = highlight
    ? styles.highlightedBox
    : compact
      ? styles.compactBox
      : styles.animalBox;

  return (
    <View style={boxStyle}>
      {animal.dgr && <Text style={styles.animalInfo}>DGR: {animal.dgr}</Text>}
      {animal.breed && <Text style={styles.animalInfo}>{animal.breed}</Text>}
      {animal.birthDate && (
        <Text style={styles.animalInfo}>{animal.birthDate}</Text>
      )}
      {label && <Text style={styles.label}>{label}</Text>}
    </View>
  );
};

// Component to render connecting lines
// Component to render connecting lines

// MilkClorifideTable for PDF
interface MilkClorifideData {
  milk_kg?: number;
  age1p?: number;
  ebi?: number;
  milk_acc?: number;
  age_1p_acc?: number;
  ebi_acc?: number;
  beta_lac?: string;
  kappa_c?: string;
  beta_c?: string;
}

const stylesPdfTable = StyleSheet.create({
  table: {
    width: "100%",
    marginTop: 8,
    marginBottom: 8,
  },
  headerRow: {
    flexDirection: "row" as const,
    backgroundColor: "#fefce8",
    borderBottomWidth: 1,
    borderBottomColor: "#e2e8f0",
    padding: 4,
  },
  row: {
    flexDirection: "row" as const,
    borderBottomWidth: 1,
    borderBottomColor: "#f1f5f9",
    padding: 4,
  },
  stripedRow: {
    backgroundColor: "#f0fdf4", // green-50
  },
  cell: {
    flex: 1,
    fontSize: 7,
    color: "#374151",
    textAlign: "center" as const,
    padding: 2,
  },
  headerCell: {
    flex: 1,
    fontSize: 8,
    fontWeight: 700 as const,
    color: "#1f2937",
    textAlign: "center" as const,
    padding: 2,
  },
});

const MilkClorifideTablePDF: React.FC<{
  data: MilkClorifideData[];
}> = ({ data }) => {
  return (
    <View style={stylesPdfTable.table}>
      {/* Header */}
      <View style={stylesPdfTable.headerRow}>
        <Text style={stylesPdfTable.headerCell}>Milk (kg)</Text>
        <Text style={stylesPdfTable.headerCell}>Age 1P</Text>
        <Text style={stylesPdfTable.headerCell}>EBI</Text>
        <Text style={stylesPdfTable.headerCell}>Milk Acc (%)</Text>
        <Text style={stylesPdfTable.headerCell}>Age 1P Acc (%)</Text>
        <Text style={stylesPdfTable.headerCell}>EBI Acc (%)</Text>
        <Text style={stylesPdfTable.headerCell}>Beta-lac</Text>
        <Text style={stylesPdfTable.headerCell}>Kappa-c</Text>
        <Text style={stylesPdfTable.headerCell}>Beta-c</Text>
      </View>
      {/* Data Rows */}
      {data && data.length > 0 ? (
        data.map((row, idx) => (
          <View
            key={idx}
            style={
              idx % 2 === 1
                ? [stylesPdfTable.row, stylesPdfTable.stripedRow]
                : stylesPdfTable.row
            }
          >
            <Text style={stylesPdfTable.cell}>{row.milk_kg ?? "-"}</Text>
            <Text style={stylesPdfTable.cell}>{row.age1p ?? "-"}</Text>
            <Text style={stylesPdfTable.cell}>{row.ebi ?? "-"}</Text>
            <Text style={stylesPdfTable.cell}>{row.milk_acc ?? "-"}</Text>
            <Text style={stylesPdfTable.cell}>{row.age_1p_acc ?? "-"}</Text>
            <Text style={stylesPdfTable.cell}>{row.ebi_acc ?? "-"}</Text>
            <Text style={stylesPdfTable.cell}>{row.beta_lac ?? "-"}</Text>
            <Text style={stylesPdfTable.cell}>{row.kappa_c ?? "-"}</Text>
            <Text style={stylesPdfTable.cell}>{row.beta_c ?? "-"}</Text>
          </View>
        ))
      ) : (
        <View style={stylesPdfTable.row}>
          <Text style={[stylesPdfTable.cell, { flex: 9 as const }]}>
            No milk analysis data available
          </Text>
        </View>
      )}
    </View>
  );
};

// Main PedigreeTree component
export function PedigreeTree({ animal }: { animal: any }) {
  if (!animal) {
    return (
      <Document>
        <Page size="A4" orientation="landscape" style={styles.page}>
          <Text style={styles.title}>No animal data available</Text>
        </Page>
      </Document>
    );
  }

  // Helper function to create animal objects from the pedigree data
  const createAnimalFromPedigree = (
    name?: string,
    dgr?: string,
    breed?: string,
  ): Animal | null => {
    if (!name) return null;
    return {
      id: dgr || name,
      name,
      breed: breed || "Unknown",
      dgr: dgr || "",
    };
  };

  // Create all animal objects
  const mainAnimal: Animal = {
    id: animal.dgr,
    name: animal.name || "N/A",
    dgr: animal.dgr,
    birthDate: animal.birthDate,
    sex: animal.sex,
    coatColor: animal.coatColor,
    weight: animal.weight,
    racialComposition: animal.racialComposition,
  };

  const sire = createAnimalFromPedigree(
    animal.sireName,
    animal.sireDGR,
    animal.sireBreed,
  );
  const dam = createAnimalFromPedigree(
    animal.damName,
    animal.damDGR,
    animal.damBreed,
  );

  const paternalGrandSire = createAnimalFromPedigree(
    animal.paternalGrandSireName,
    animal.paternalGrandSireDGR,
    animal.paternalGrandSireBreed,
  );
  const paternalGrandDam = createAnimalFromPedigree(
    animal.paternalGrandDamName,
    animal.paternalGrandDamDGR,
    animal.paternalGrandDamBreed,
  );
  const maternalGrandSire = createAnimalFromPedigree(
    animal.maternalGrandSireName,
    animal.maternalGrandSireDGR,
    animal.maternalGrandSireBreed,
  );
  const maternalGrandDam = createAnimalFromPedigree(
    animal.maternalGrandDamName,
    animal.maternalGrandDamDGR,
    animal.maternalGrandDamBreed,
  );

  // Great-grandparents
  const paternalGrandSireGrandSire = createAnimalFromPedigree(
    animal.paternalGrandSireGrandSireName,
    animal.paternalGrandSireGrandSireDGR,
    animal.paternalGrandSireGrandSireBreed,
  );
  const paternalGrandSireGrandDam = createAnimalFromPedigree(
    animal.paternalGrandSireGrandDamName,
    animal.paternalGrandSireGrandDamDGR,
    animal.paternalGrandSireGrandDamBreed,
  );
  const paternalGrandDamGrandSire = createAnimalFromPedigree(
    animal.paternalGrandDamGrandSireName,
    animal.paternalGrandDamGrandSireDGR,
    animal.paternalGrandDamGrandSireBreed,
  );
  const paternalGrandDamGrandDam = createAnimalFromPedigree(
    animal.paternalGrandDamGrandDamName,
    animal.paternalGrandDamGrandDamDGR,
    animal.paternalGrandDamGrandDamBreed,
  );
  const maternalGrandSireGrandSire = createAnimalFromPedigree(
    animal.maternalGrandSireGrandSireName,
    animal.maternalGrandSireGrandSireDGR,
    animal.maternalGrandSireGrandSireBreed,
  );
  const maternalGrandSireGrandDam = createAnimalFromPedigree(
    animal.maternalGrandSireGrandDamName,
    animal.maternalGrandSireGrandDamDGR,
    animal.maternalGrandSireGrandDamBreed,
  );
  const maternalGrandDamGrandSire = createAnimalFromPedigree(
    animal.maternalGrandDamGrandSireName,
    animal.maternalGrandDamGrandSireDGR,
    animal.maternalGrandDamGrandSireBreed,
  );
  const maternalGrandDamGrandDam = createAnimalFromPedigree(
    animal.maternalGrandDamGrandDamName,
    animal.maternalGrandDamGrandDamDGR,
    animal.maternalGrandDamGrandDamBreed,
  );

  return (
    <View style={styles.treeContainer}>
      {/* Add the connecting lines */}
      {/* <ConnectingLines /> */}
      {/* Generation 1 - Main Animal */}
      <View style={styles.generation}>
        <View style={{ flex: 1 }} />
        <AnimalBox animal={mainAnimal} highlight />
        <View style={{ flex: 1 }} />
      </View>

      {/* Generation 2 - Parents */}
      <View style={styles.generation}>
        <AnimalBox animal={sire} label="Sire" />
        <View style={{ flex: 1 }} />
        <AnimalBox animal={dam} label="Dam" />
      </View>

      {/* Generation 3 - Grandparents */}
      <View style={styles.generation}>
        <AnimalBox animal={paternalGrandSire} label="Pat. Grand Sire" compact />
        <AnimalBox animal={paternalGrandDam} label="Pat. Grand Dam" compact />
        <AnimalBox animal={maternalGrandSire} label="Mat. Grand Sire" compact />
        <AnimalBox animal={maternalGrandDam} label="Mat. Grand Dam" compact />
      </View>

      {/* Generation 4 - Great-Grandparents */}
      <View style={styles.generation}>
        <AnimalBox animal={paternalGrandSireGrandSire} label="PGS-GS" compact />
        <AnimalBox animal={paternalGrandSireGrandDam} label="PGS-GD" compact />
        <AnimalBox animal={paternalGrandDamGrandSire} label="PGD-GS" compact />
        <AnimalBox animal={paternalGrandDamGrandDam} label="PGD-GD" compact />
        <AnimalBox animal={maternalGrandSireGrandSire} label="MGS-GS" compact />
        <AnimalBox animal={maternalGrandSireGrandDam} label="MGS-GD" compact />
        <AnimalBox animal={maternalGrandDamGrandSire} label="MGD-GS" compact />
        <AnimalBox animal={maternalGrandDamGrandDam} label="MGD-GD" compact />
      </View>
    </View>
  );
}
